import React from "react";
import Image from "next/image";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";

const Title = ({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) => (
  <h2 className={cn("text-6xl laptop:text-7xl", className)}>{children}</h2>
);

const Chart = ({
  className,
  chart,
}: {
  className: string;
  chart: string[][];
}) => {
  return (
    <div className={cn(className)}>
      <div className="grid grid-flow-row auto-rows-auto">
        {chart.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={cn(
              "grid auto-cols-fr grid-flow-col first:bg-coolgray-10",
            )}
          >
            {row.map((item, index) => (
              <p
                key={index}
                className="whitespace-pre-wrap p-2 text-sm laptop:px-4 laptop:py-5 laptop:text-base"
              >
                {item}
              </p>
            ))}
          </div>
        ))}
      </div>
      <span className="block h-[2px] w-full bg-coolgray-10" />
    </div>
  );
};

const InchChart: string[][] = [
  ["inch Size", "Small", "Medium", "Large", "X-Large"],
  ["C", "8”", "8.7”", "9.5”", "10.2”"],
  ["L", "2.7”", "3,1”", "3.4”", "3.6”"],
];

const CmChart = [
  ["cm Size", "Small", "Medium", "Large", "X-Large"],
  ["C", "20cm", "22cm", "24cm", "26cm"],
  ["L", "7cm", "7.8cm", "8.6cm", "9.2cm"],
];

const translate = (chart: string[][], t: (key: string) => string) =>
  chart.map((row) => row.map((item) => t(item)));

const TactGloveSizing = ({ className }: { className?: string }) => {
  const { t } = useTrans(["tactsuit"]);
  const InchChartTranslated = translate(InchChart, t);
  const CmChartTranslated = translate(CmChart, t);

  return (
    <div className={cn("w-full", className)}>
      <div className="flex h-[218px] gap-x-4 laptop:h-[336px] laptop:gap-x-9">
        <div className="relative h-full w-[156px] laptop:w-[240px]">
          <Image src="/images/tactsuit/glove-size-guide_1.jpg" alt="" fill />
        </div>
        <div className="relative h-full w-[156px] laptop:w-[240px]">
          <Image src="/images/tactsuit/glove-size-guide_2.jpg" alt="" fill />
        </div>
      </div>
      <Title className="mt-6 text-center laptop:text-left">
        {t("4 Different Sizes (S, M, L, XL)")}
      </Title>
      <Chart className="mt-5 laptop:mt-6" chart={InchChartTranslated} />
      <Chart className="mt-5 laptop:mt-6" chart={CmChartTranslated} />
      <Sizes className="mt-5 laptop:mt-6" />
      <div className="mt-2">
        <Title className="!text-base text-coolgray-60">
          {t("* Main Glove Only (excluding inner glove)")}
        </Title>
        <Title className="!text-base text-coolgray-60">
          {t(
            "* If you are in between sizes, we recommend sizing down for a proper fit.",
          )}
        </Title>
      </div>
    </div>
  );
};

const Sizes = ({ className }: { className?: string }) => {
  return (
    <div className={cn("w-full text-lg", className)}>
      <ul>
        <li>S: 3.88 oz (110 g)</li>
        <li>M: 3.95 oz (112 g)</li>
        <li>L: 4.02 oz (114 g)</li>
        <li>XL: 4.09 oz (116 g)</li>
      </ul>
    </div>
  );
};

export default TactGloveSizing;
